import { useMutation } from "@apollo/client";
import { FormikHelpers, Formik, Form } from "formik";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";

import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikSwitchField from "src/components/form/FormikSwitchField";
import FormikTextField from "src/components/form/FormikTextField";
import SubmitButton from "src/components/form/SubmitButton";
import {
  twilioSubaccountCreate,
  twilioSubaccountCreateVariables
} from "src/graphql/generated/twilioSubaccountCreate";
import { CREATE_TWILIO_SUBACCOUNT } from "src/graphql/twilioSubaccount";

type Values = {
  name: string;
  shareAcrossTenants: boolean;
};

export default function GenerateTwilioConfig({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const snackbar = useSnackbar();

  const schema = yup.object().shape({
    name: yup.string().required("Required"),
    shareAcrossTenants: yup.boolean().required("Required")
  });

  const [autoCreateTwilioSubaccount] = useMutation<
    twilioSubaccountCreate,
    twilioSubaccountCreateVariables
  >(CREATE_TWILIO_SUBACCOUNT, {
    refetchQueries: ["listTwilioSubaccounts"]
  });

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    try {
      const { errors } = await autoCreateTwilioSubaccount({
        variables: {
          ...values
        }
      });

      if (!errors) {
        snackbar.enqueueSnackbar("Twilio configuration created.", {
          variant: "info"
        });
        helpers.resetForm({
          values: {
            name: "",
            shareAcrossTenants: false
          }
        });
      }
    } catch (errors) {
      console.error(errors);
      snackbar.enqueueSnackbar("Failed to create Twilio configuration.", {
        variant: "error"
      });
    }
  }

  return (
    <Dialog open={open}>
      <DialogTitle>Create New Twilio Subaccount</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            name: "",
            shareAcrossTenants: false
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <FormikErrorBanner />
              <FormikTextField
                fullWidth
                label="Friendly Name"
                name="name"
                disabled={isSubmitting}
              />
              <FormikSwitchField
                label="Share across tenants"
                name="shareAcrossTenants"
                disabled={isSubmitting}
              />
              <DialogActions>
                <SubmitButton
                  type="submit"
                  disabled={!dirty}
                  isSubmitting={isSubmitting}
                  submitText="Create"
                  loadingText="Creating..."
                />
                <Button
                  disabled={isSubmitting}
                  onClick={onClose}
                  color="primary"
                >
                  Cancel
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
